import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import History from './History';

const API_URL = 'https://jxbk.jingchaowan.dpdns.org';

const MainContent = ({ selectedOperator, user }) => {
    const [kpiTemplate, setKpiTemplate] = useState([]);
    const [performanceData, setPerformanceData] = useState({});
    const [scores, setScores] = useState({});
    const [totalScore, setTotalScore] = useState(0);
    const [egpScore, setEgpScore] = useState(1);
    const [finalScore, setFinalScore] = useState(0);
    const [processScoreTotal, setProcessScoreTotal] = useState(0);
    const [managementScoreTotal, setManagementScoreTotal] = useState(0);
    const [validationMessages, setValidationMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('main'); // 'main' or 'history'

    const verificationKpi = useMemo(() =>
        kpiTemplate.find(item => item.indicator.includes('核销总目标') || item.kpi.includes('核销总目标')),
        [kpiTemplate]);

    const calculateScores = useCallback((template, perfData, operator) => {
        const newScores = {};
        let processTotal = 0;
        let managementTotal = 0;
        const missingFields = [];

        template.forEach(item => {
            // Check if field is empty (undefined, null, or empty string), but allow 0
            const value = perfData[item.editable_field_key];
            const isEmpty = value === undefined || value === null || value === '';
            if (!item.is_auto_calculated && isEmpty) {
                missingFields.push(`"${item.indicator}" 的 "上月数据"`);
            }

            try {
                let score = 0;
                // 支持 "管理" 和 "过程" 类指标直接读取数值作为得分（如果没有公式）
                if (item.category.includes('管理') || (item.category.includes('过程') && !item.formula)) {
                    score = parseFloat(perfData[item.editable_field_key]) || 0;
                } else if (item.formula) {
                    const formula = item.formula;
                    const weight = item.weight;
                    const avg_score = perfData.jingyingfen !== undefined && perfData.jingyingfen !== '' ? parseFloat(perfData.jingyingfen) : (perfData.manual_avg_score !== undefined ? perfData.manual_avg_score : (operator?.avg_score || 0));

                    let total_salary = operator?.total_salary || 0;
                    if ((item.indicator.includes('核销总目标') || item.kpi.includes('核销总目标')) && !item.is_auto_calculated) {
                        total_salary = parseFloat(perfData['verification_total']) || 0;
                    }

                    const quit_store_count = parseFloat(perfData?.quit_store_count) || 0;
                    const sales_total = parseFloat(perfData?.sales_total) || 0;

                    const working_days = parseFloat(perfData.manage_last_month_20) || 30;
                    const context = { weight, avg_score, total_salary, quit_store_count, sales_total, working_days, ...perfData };
                    const contextKeys = Object.keys(context).filter(key => /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(key));
                    const contextValues = contextKeys.map(key => {
                        const val = context[key];
                        // 尝试将数字字符串转换为浮点数，否则使用原值或 0
                        return (typeof val === 'string' && !isNaN(val) && val.trim() !== '') ? parseFloat(val) : (val || 0);
                    });

                    score = new Function(...contextKeys, `return ${formula}`)(...contextValues);
                }

                newScores[item.id] = score;

                if (item.category.includes('经营') || item.category.includes('过程')) {
                    processTotal += score;
                } else if (item.category.includes('管理')) {
                    managementTotal += score;
                }
            } catch (e) {
                console.error(`计算指标 "${item.indicator}" 的得分时出错:`, e);
                newScores[item.id] = 0;
            }
        });
        setScores(newScores);
        setProcessScoreTotal(processTotal);
        setManagementScoreTotal(managementTotal);
        setTotalScore(processTotal + managementTotal);
        setValidationMessages(missingFields);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedOperator) return;
            setView('main'); // Reset to main view when operator changes
            setLoading(true);
            try {
                const date = new Date();
                const lastMonthDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
                const month = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

                const [templateRes, performanceRes] = await Promise.all([
                    fetch(`${API_URL}/kpi-template?person=${selectedOperator.operator_name}`),
                    fetch(`${API_URL}/performance?month=${month}&person=${selectedOperator.operator_name}`)
                ]);

                if (!templateRes.ok) throw new Error('获取考核模板失败');
                if (!performanceRes.ok) throw new Error('获取考核数据失败');

                const templateData = await templateRes.json();
                const perfData = await performanceRes.json();

                setKpiTemplate(templateData || []);
                setPerformanceData(perfData || {});
                setEgpScore(perfData?.egp_score !== undefined ? perfData.egp_score : 1);
                calculateScores(templateData || [], perfData || {}, selectedOperator);

            } catch (error) {
                console.error(error);
                alert(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedOperator, calculateScores]);

    useEffect(() => {
        setFinalScore(totalScore * egpScore);
    }, [totalScore, egpScore]);

    const handleDataChange = (key, value) => {
        const newData = { ...performanceData, [key]: value };
        setPerformanceData(newData);
        calculateScores(kpiTemplate, newData, selectedOperator);
    };

    const handleTemplateChange = (id, field, value) => {
        const updatedTemplate = kpiTemplate.map(item =>
            item.id === id ? { ...item, [field]: field === 'weight' ? parseInt(value) || 0 : value } : item
        );
        setKpiTemplate(updatedTemplate);
        // 模板结构变动后，重新计算得分
        calculateScores(updatedTemplate, performanceData, selectedOperator);
    };

    const handleAddIndicator = (category) => {
        // 自动分配管理指标的存储字段
        let newFieldKey = null;
        if (category.includes('管理')) {
            const usedKeys = kpiTemplate.map(it => it.editable_field_key).filter(k => k && k.startsWith('manage_last_month_'));
            for (let i = 1; i <= 20; i++) {
                const key = `manage_last_month_${i}`;
                if (!usedKeys.includes(key)) {
                    newFieldKey = key;
                    break;
                }
            }
        } else if (category.includes('过程')) {
            // 过程指标如果没有公式，则作为手动输入项，这里可以默认不指定 key，由用户在 formula 处扩展，或分配通用字段
            // 这里为了简单，新增项默认手动输入得分
        }

        const newItem = {
            id: Date.now(), // 临时前端ID
            person_name: selectedOperator.operator_name,
            category: category,
            indicator: '新指标名称',
            kpi: '新考核标准',
            weight: 0,
            is_auto_calculated: 0,
            editable_field_key: newFieldKey
        };
        setKpiTemplate([...kpiTemplate, newItem]);
    };

    const handleDeleteIndicator = (item) => {
        if (!window.confirm(`确定要删除指标 "${item.indicator || '未命名'}" 吗？此操作在保存模板结构前不会生效。`)) return;
        setKpiTemplate(kpiTemplate.filter(it => it.id !== item.id));
    };

    const handleSaveTemplate = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/kpi-template/update-items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: kpiTemplate,
                    person_name: selectedOperator.operator_name
                }),
            });
            if (!response.ok) throw new Error('保存模板失败');
            alert('模板结构保存成功！');
        } catch (error) {
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedOperator) return;
        setLoading(true);
        try {
            const date = new Date();
            const lastMonthDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
            const month = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

            // Convert ID-based scores back to KPI-name-based scores for storage and history display
            const readableScores = {};
            kpiTemplate.forEach(item => {
                if (scores[item.id] !== undefined) {
                    readableScores[item.kpi] = scores[item.id];
                }
            });

            const payload = {
                ...performanceData,
                performance_month: month,
                person_name: selectedOperator.operator_name,
                scores: JSON.stringify(readableScores),
                total_score: totalScore,
                final_score: finalScore
            };

            const response = await fetch(`${API_URL}/performance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('保存失败');
            alert('保存成功！');
        } catch (error) {
            console.error(error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const totalWeight = useMemo(() => {
        return kpiTemplate.reduce((sum, item) => sum + (parseInt(item.weight) || 0), 0);
    }, [kpiTemplate]);

    const groupedTemplate = useMemo(() => {
        return kpiTemplate.reduce((acc, item) => {
            const category = item.category || '其他';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(item);
            return acc;
        }, {});
    }, [kpiTemplate]);

    if (!selectedOperator) {
        return <div className="main-content"><h1>绩效考核表</h1><p>请在左侧选择一位运营人员。</p></div>;
    }

    if (loading) {
        return <div className="main-content"><h1>正在加载考核数据...</h1></div>;
    }

    if (view === 'history') {
        return <History selectedOperator={selectedOperator} onBack={() => setView('main')} />;
    }

    const getLastMonthValue = (item) => {
        if (item.is_auto_calculated) {
            if (item.indicator.includes('核销总目标') || item.kpi.includes('核销总目标')) {
                return `${(selectedOperator.total_salary || 0).toFixed(2)}元`;
            }
            return 'N/A';
        }

        let key_to_use = item.editable_field_key;
        if (item.indicator.includes('核销总目标') || item.kpi.includes('核销总目标')) {
            key_to_use = 'verification_total';
        } else if (item.indicator.includes('经营分') || item.kpi.includes('经营分')) {
            key_to_use = 'jingyingfen';
        }

        if (!key_to_use) {
            return 'N/A (配置错误)';
        }

        if (user?.role !== 'admin') {
            return performanceData[key_to_use] !== undefined && performanceData[key_to_use] !== null ? performanceData[key_to_use] : '';
        }

        const currentValue = performanceData[key_to_use];
        const isExceeding = !item.is_auto_calculated && currentValue !== undefined && currentValue !== '' && parseFloat(currentValue) > item.weight;

        return <input
            type="text"
            value={currentValue !== undefined && currentValue !== null ? currentValue : ''}
            onChange={(e) => handleDataChange(key_to_use, e.target.value)}
            style={{
                border: isExceeding ? '2px solid #ff4d4f' : '1px solid #ccc',
                backgroundColor: isExceeding ? '#fff2f0' : 'white',
                color: isExceeding ? '#ff4d4f' : 'inherit'
            }}
            title={isExceeding ? `警告：填入数值不能超过权重 (${item.weight})` : ''}
        />;
    };

    const getRemarksValue = (item) => {
        let key = item.editable_field_key;
        if (!key) return '';

        let remarksKey = null;
        if (key.includes('last_month')) {
            remarksKey = key.replace('last_month', 'remarks');
        } else if (key === 'sales_total' || key === 'verification_total' || key === 'quit_store_count' || key === 'jingyingfen') {
            remarksKey = `${key}_remarks`;
        } else {
            // 如果是其他自定义 key，也自动加上 _remarks
            remarksKey = `${key}_remarks`;
        }

        return performanceData[remarksKey] || '';
    };

    const handleRemarksChange = (item, value) => {
        let key = item.editable_field_key;
        if (!key) return;

        let remarksKey = null;
        if (key.includes('last_month')) {
            remarksKey = key.replace('last_month', 'remarks');
        } else if (key === 'sales_total' || key === 'verification_total' || key === 'quit_store_count' || key === 'jingyingfen') {
            remarksKey = `${key}_remarks`;
        } else {
            remarksKey = `${key}_remarks`;
        }

        if (remarksKey) {
            const newData = { ...performanceData, [remarksKey]: value };
            setPerformanceData(newData);
        }
    };

    const handleEgpChange = (e) => {
        const newEgpScore = parseFloat(e.target.value);
        setEgpScore(newEgpScore);
        handleDataChange('egp_score', newEgpScore);
    };

    const handleToggleAutoCalculate = async () => {
        if (!selectedOperator || !verificationKpi) return;

        const newIsAutoCalculated = verificationKpi.is_auto_calculated === 1 ? 0 : 1;

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/kpi-template/set-auto-calculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    person_name: selectedOperator.operator_name,
                    indicator: verificationKpi.indicator,
                    is_auto_calculated: newIsAutoCalculated
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || '更新失败');
            }

            const updatedTemplate = kpiTemplate.map(item =>
                item.id === verificationKpi.id
                    ? { ...item, is_auto_calculated: newIsAutoCalculated }
                    : item
            );
            setKpiTemplate(updatedTemplate);

            // Recalculate scores with the updated template and existing data
            calculateScores(updatedTemplate, performanceData, selectedOperator);

            alert(`成功切换到${newIsAutoCalculated === 0 ? '手动' : '自动'}核销模式。`);
        } catch (error) {
            console.error(error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-content">
            <div className="main-header">
                <h1>{`${new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getFullYear()}年${new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getMonth() + 1}月 ${selectedOperator.operator_name} 绩效考核表`}</h1>
                <div className="header-actions">
                    <div className="total-score">
                        <small>月度绩效结果</small>
                        <div><span>{finalScore.toFixed(2)}</span></div>
                        <small>总得分: {totalScore.toFixed(2)} × EGP: {egpScore}</small>
                    </div>
                    <div className="egp-selector" style={{ margin: '0 15px', display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="egp-score" style={{ marginRight: '5px' }}>EGP 评分: </label>
                        <select id="egp-score" value={egpScore ?? ''} onChange={handleEgpChange} disabled={user?.role !== 'admin'}>
                            <option value="1.2">1.2</option>
                            <option value="1">1</option>
                            <option value="0.8">0.8</option>
                            <option value="0">0</option>
                        </select>
                    </div>
                    {verificationKpi && user?.role === 'admin' && (
                        <button
                            className="toggle-auto-calc-button"
                            onClick={handleToggleAutoCalculate}
                            style={{ marginLeft: '10px', backgroundColor: verificationKpi.is_auto_calculated ? '#28a745' : '#ffc107', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer' }}
                            disabled={loading}
                        >
                            {verificationKpi.is_auto_calculated ? '无刷单(自动核销)' : '有刷单(手动核销)'}
                        </button>
                    )}
                    {user?.role === 'admin' && (
                        <>
                            <div className="weight-counter" style={{
                                backgroundColor: totalWeight === 100 ? '#e6fffa' : '#fff5f5',
                                border: `1px solid ${totalWeight === 100 ? '#38b2ac' : '#feb2b2'}`,
                                color: totalWeight === 100 ? '#2c7a7b' : '#c53030',
                                padding: '5px 15px',
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}>
                                权重总和: {totalWeight} {totalWeight === 100 ? '✅' : '❌'}
                            </div>
                            <button className="save-button" onClick={handleSaveTemplate} style={{ marginRight: '10px', backgroundColor: '#722ed1' }} disabled={loading}>
                                {loading ? '正在同步...' : '保存模板结构'}
                            </button>
                            <button className="save-button" onClick={handleSave} disabled={loading}>
                                {loading ? '正在保存...' : '保存当月数据'}
                            </button>
                        </>
                    )}
                    <button className="view-history-button" onClick={() => setView('history')} style={{ marginLeft: '10px' }}>
                        查看历史
                    </button>
                </div>
            </div>

            {validationMessages.length > 0 && (
                <div className="validation-warning">
                    <p>请填写以下必填项：</p>
                    <ul>
                        {validationMessages.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                </div>
            )}

            {Object.entries(groupedTemplate).map(([category, items]) => (
                <div className="table-container" key={category}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2>{category}</h2>
                        {user?.role === 'admin' && (
                            <button
                                onClick={() => handleAddIndicator(category)}
                                style={{ fontSize: '12px', padding: '4px 12px', backgroundColor: '#e6f7ff', color: '#1890ff', border: '1px solid #91d5ff', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                + 新增指标
                            </button>
                        )}
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '20%' }}>经营指标</th>
                                <th>关键考核指标</th>
                                <th style={{ width: '100px' }}>权重</th>
                                <th style={{ width: '15%' }}>上月数据</th>
                                <th style={{ width: '100px' }}>得分</th>
                                <th>备注</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.id}>
                                    <td>
                                        {user?.role === 'admin' ? (
                                            <input
                                                type="text"
                                                value={item.indicator || ''}
                                                onChange={(e) => handleTemplateChange(item.id, 'indicator', e.target.value)}
                                                className="template-input"
                                            />
                                        ) : item.indicator}
                                    </td>
                                    <td>
                                        {user?.role === 'admin' ? (
                                            <textarea
                                                value={item.kpi || ''}
                                                onChange={(e) => handleTemplateChange(item.id, 'kpi', e.target.value)}
                                                className="template-textarea"
                                            />
                                        ) : item.kpi}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {user?.role === 'admin' ? (
                                            <input
                                                type="number"
                                                value={item.weight || 0}
                                                onChange={(e) => handleTemplateChange(item.id, 'weight', e.target.value)}
                                                style={{ width: '70px', textAlign: 'center' }}
                                            />
                                        ) : item.weight}
                                    </td>
                                    <td>{getLastMonthValue(item)}</td>
                                    <td>{(scores[item.id] || 0).toFixed(2)}</td>
                                    <td>
                                        {user?.role === 'admin' ? (
                                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    value={getRemarksValue(item)}
                                                    onChange={(e) => handleRemarksChange(item, e.target.value)}
                                                />
                                                {!item.is_auto_calculated && (
                                                    <button
                                                        onClick={() => handleDeleteIndicator(item)}
                                                        style={{ color: '#ff4d4f', border: 'none', background: 'none', cursor: 'pointer', fontSize: '18px' }}
                                                        title="删除指标"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            getRemarksValue(item)
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}

            {/* 使用 Portal 将出勤天数设置项移至侧边栏挂载点 */}
            {document.getElementById('sidebar-working-days-portal') && createPortal(
                <div className="sidebar-section" style={{ backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                    <label style={{ color: '#856404', fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                        🗓️ 当月出勤天数
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            type="number"
                            value={performanceData.manage_last_month_20 || 30}
                            onChange={(e) => handleDataChange('manage_last_month_20', e.target.value)}
                            style={{
                                width: '80px',
                                padding: '6px',
                                borderRadius: '4px',
                                border: '1px solid #ffd666',
                                textAlign: 'center',
                                fontSize: '16px',
                                fontWeight: 'bold'
                            }}
                        />
                        <span style={{ fontSize: '12px', color: '#999' }}>天 (默认30)</span>
                    </div>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#b78103' }}>
                        * 仅对本月业务部指标生效
                    </p>
                </div>,
                document.getElementById('sidebar-working-days-portal')
            )}
        </div>
    );
};

export default MainContent;
